import type { ReactNode } from 'react'

// Diff rendering for suggestion comments (spec §8.2). Per-comment state
// (which mode is active) lives on the parent (CommentCard) — this component
// is fully controlled via `mode`/`onToggle`, matching A2's "positioned by
// parent" convention for everything else in the gutter.

export type DiffMode = 'inline' | 'side'

const TOGGLE_ACTIVE = { background: 'var(--cd-text)', color: 'var(--cd-bg)' }
const TOGGLE_INACTIVE = {
  background: 'transparent',
  color: 'var(--cd-text-muted)',
}

function splitLines(text: string): string[] {
  return text.split('\n')
}

export function DiffBlock(props: {
  diff: { before: string; after: string }
  mode: DiffMode
  onToggle: (mode: DiffMode) => void
}): ReactNode {
  const { diff, mode, onToggle } = props
  const before = splitLines(diff.before)
  const after = splitLines(diff.after)

  return (
    <div className="mt-2">
      <div className="mb-1.5 flex gap-1">
        <button
          type="button"
          aria-pressed={mode === 'inline'}
          onClick={() => onToggle('inline')}
          className="rounded-md border border-(--cd-border-2) px-2.5 py-0.5 text-[10.5px] font-semibold"
          style={mode === 'inline' ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}
        >
          Inline
        </button>
        <button
          type="button"
          aria-pressed={mode === 'side'}
          onClick={() => onToggle('side')}
          className="rounded-md border border-(--cd-border-2) px-2.5 py-0.5 text-[10.5px] font-semibold"
          style={mode === 'side' ? TOGGLE_ACTIVE : TOGGLE_INACTIVE}
        >
          Side-by-side
        </button>
      </div>

      {mode === 'inline' ? (
        <div
          data-testid="diff-unified"
          className="overflow-x-auto rounded-lg border border-(--cd-border) bg-(--cd-bg) py-2"
        >
          {before.map((line, i) => (
            <div
              key={`before-${i}-${line}`}
              className="whitespace-pre px-3 font-mono text-[11.5px] leading-[1.7]"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-red) 9%, transparent)',
                color: 'var(--cd-red-soft)',
              }}
            >
              - {line}
            </div>
          ))}
          {after.map((line, i) => (
            <div
              key={`after-${i}-${line}`}
              className="whitespace-pre px-3 font-mono text-[11.5px] leading-[1.7]"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-green) 9%, transparent)',
                color: 'var(--cd-green-soft)',
              }}
            >
              + {line}
            </div>
          ))}
        </div>
      ) : (
        <div
          data-testid="diff-side-by-side"
          className="grid grid-cols-2 gap-1.5"
        >
          <div
            className="overflow-x-auto rounded-lg border py-2"
            style={{
              borderColor:
                'color-mix(in oklab, var(--cd-red) 25%, transparent)',
              background: 'var(--cd-bg)',
            }}
          >
            {before.map((line, i) => (
              <div
                key={`old-${i}-${line}`}
                className="whitespace-pre px-2.5 font-mono text-[10.5px] leading-[1.7]"
                style={{ color: 'var(--cd-red-soft)' }}
              >
                {line}
              </div>
            ))}
          </div>
          <div
            className="overflow-x-auto rounded-lg border py-2"
            style={{
              borderColor:
                'color-mix(in oklab, var(--cd-green) 25%, transparent)',
              background: 'var(--cd-bg)',
            }}
          >
            {after.map((line, i) => (
              <div
                key={`new-${i}-${line}`}
                className="whitespace-pre px-2.5 font-mono text-[10.5px] leading-[1.7]"
                style={{ color: 'var(--cd-green-soft)' }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
